import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState('verifying'); // verifying | invalid | confirm | done | resubscribed | error
  const [error, setError] = useState('');
  const [alreadyUnsubscribed, setAlreadyUnsubscribed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const verify = useCallback(async () => {
    if (!token) { setStatus('invalid'); return; }
    try {
      const r = await fetch(`${API_URL}/api/unsubscribe?token=${encodeURIComponent(token)}`);
      if (!r.ok) {
        setStatus('invalid');
        return;
      }
      const data = await r.json();
      setAlreadyUnsubscribed(Boolean(data.already_unsubscribed));
      setStatus('confirm');
    } catch (e) {
      setError(e.message || 'Failed to verify link');
      setStatus('error');
    }
  }, [token]);

  useEffect(() => { verify(); }, [verify]);

  const handleUnsubscribe = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/unsubscribe?token=${encodeURIComponent(token)}`, { method: 'POST' });
      if (!r.ok) throw new Error(`Server returned ${r.status}`);
      setStatus('done');
    } catch (e) {
      setError(e.message || 'Failed to unsubscribe');
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResubscribe = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/unsubscribe/resubscribe?token=${encodeURIComponent(token)}`, { method: 'POST' });
      if (!r.ok) throw new Error(`Server returned ${r.status}`);
      setStatus('resubscribed');
    } catch (e) {
      setError(e.message || 'Failed to resubscribe');
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-gray-800 rounded-xl border border-gray-700 p-6 sm:p-8" data-testid="unsubscribe-card">
        <div className="text-center mb-6">
          <div className="text-2xl font-bold text-white">MyInvestFolio</div>
          <div className="text-xs text-gray-500 mt-1">Email preferences</div>
        </div>

        {status === 'verifying' && (
          <div className="flex flex-col items-center gap-3 py-6" data-testid="unsubscribe-verifying">
            <Loader2 className="w-8 h-8 animate-spin text-green-400" />
            <div className="text-sm text-gray-400">Verifying your link…</div>
          </div>
        )}

        {status === 'invalid' && (
          <div className="text-center py-6 space-y-3" data-testid="unsubscribe-invalid">
            <XCircle className="w-12 h-12 text-red-400 mx-auto" />
            <div className="text-lg font-semibold text-white">Invalid or expired link</div>
            <div className="text-sm text-gray-400">
              This unsubscribe link is no longer valid. If you keep receiving unwanted emails, contact us at <span className="text-green-400">hello@myinvestfolio.io</span>.
            </div>
          </div>
        )}

        {status === 'confirm' && !alreadyUnsubscribed && (
          <div className="space-y-4" data-testid="unsubscribe-confirm">
            <div className="text-center">
              <div className="text-lg font-semibold text-white mb-1">Unsubscribe from support emails?</div>
              <div className="text-sm text-gray-400">
                We'll stop sending you follow-up updates. <strong className="text-gray-200">Login alerts, password resets, and other transactional emails will still be delivered.</strong>
              </div>
            </div>
            <button
              onClick={handleUnsubscribe}
              disabled={submitting}
              className="w-full px-4 py-2.5 bg-red-600 hover:bg-red-500 disabled:bg-gray-700 text-white rounded-lg text-sm font-medium transition flex items-center justify-center gap-1.5"
              data-testid="unsubscribe-confirm-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Yes, unsubscribe me
            </button>
            <div className="text-center text-xs text-gray-500">
              Clicked by mistake? Just close this page — nothing changes until you confirm.
            </div>
          </div>
        )}

        {status === 'confirm' && alreadyUnsubscribed && (
          <div className="space-y-4" data-testid="unsubscribe-already">
            <div className="text-center space-y-2">
              <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto" />
              <div className="text-lg font-semibold text-white">You're already unsubscribed</div>
              <div className="text-sm text-gray-400">
                You're not receiving support emails from us. If you'd like to resume, click below.
              </div>
            </div>
            <button
              onClick={handleResubscribe}
              disabled={submitting}
              className="w-full px-4 py-2.5 bg-green-600 hover:bg-green-500 disabled:bg-gray-700 text-white rounded-lg text-sm font-medium transition flex items-center justify-center gap-1.5"
              data-testid="resubscribe-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Resubscribe
            </button>
          </div>
        )}

        {status === 'done' && (
          <div className="text-center py-6 space-y-3" data-testid="unsubscribe-done">
            <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto" />
            <div className="text-lg font-semibold text-white">You've been unsubscribed</div>
            <div className="text-sm text-gray-400">
              We won't send you support emails anymore. Transactional emails (login alerts, password resets, etc.) will still be delivered.
            </div>
            <button
              onClick={handleResubscribe}
              disabled={submitting}
              className="mt-3 text-xs text-gray-500 hover:text-gray-300 underline"
              data-testid="resubscribe-after-unsub-btn"
            >
              Changed your mind? Resubscribe
            </button>
          </div>
        )}

        {status === 'resubscribed' && (
          <div className="text-center py-6 space-y-3" data-testid="unsubscribe-resubscribed">
            <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto" />
            <div className="text-lg font-semibold text-white">Welcome back</div>
            <div className="text-sm text-gray-400">You'll receive our follow-up updates again.</div>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center py-6 space-y-3" data-testid="unsubscribe-error">
            <XCircle className="w-12 h-12 text-red-400 mx-auto" />
            <div className="text-lg font-semibold text-white">Something went wrong</div>
            <div className="text-sm text-gray-400">{error}</div>
            <button
              onClick={verify}
              className="mt-3 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg"
              data-testid="unsubscribe-retry"
            >
              Try again
            </button>
          </div>
        )}

        <div className="mt-8 pt-4 border-t border-gray-700 text-center text-[11px] text-gray-500 leading-relaxed">
          MyInvestFolio<br />
          No. 8 Rasheed Alaba Williams Street, Lekki Phase 1, Lagos, Nigeria
        </div>
      </div>
    </div>
  );
}
